import { useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Button,
  Card,
  CardContent,
  Checkbox,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Skeleton,
} from '@billda/design-system';
import { ChevronDownIcon, GroupsIcon, PersonIcon } from '@billda/design-system';
import { format, parseISO } from 'date-fns';
import type { OpenPotDto } from '@billda/api';
import { routeBuilders } from '../../../routes';
import { formatCurrency } from '../../../shared/utils/currency';
import { TreeConnectorBox } from '../../../shared/components/grouped-view';
import { PotStatusBadge } from '../../finance/components/PotStatusBadge';
import { useGetOpenPots } from '../hooks/useGetOpenPots';
import { formatWeekLabel } from '../utils/periodFormatting';
import {
  ACTION_STATUSES,
  filterByStatuses,
  getNextAction,
  groupOpenPotsByWeek,
  type ActionPotStatus,
  type OpenPotsWeekGroup,
} from '../utils/groupOpenPotsByWeek';

const STATUS_PARAM = 'potStatus';

export function OpenPotsWidget() {
  const navigate = useNavigate();
  const { data, isLoading } = useGetOpenPots();
  const [searchParams, setSearchParams] = useSearchParams();

  const selectedStatuses = parseStatuses(searchParams.get(STATUS_PARAM));

  const weeks = useMemo(() => {
    const pots = data?.pots ?? [];
    return groupOpenPotsByWeek(filterByStatuses(pots, selectedStatuses));
  }, [data, selectedStatuses]);

  const setStatuses = (next: ActionPotStatus[]) => {
    setSearchParams(prev => {
      const params = new URLSearchParams(prev);
      if (next.length === ACTION_STATUSES.length) {
        params.delete(STATUS_PARAM);
      } else {
        params.set(STATUS_PARAM, next.join(','));
      }
      return params;
    });
  };

  // The "All" option toggles the whole set: select everything unless it's
  // already all selected, in which case clear it.
  const handleToggleAll = () => {
    const next =
      selectedStatuses.length === ACTION_STATUSES.length
        ? []
        : [...ACTION_STATUSES];
    setStatuses(next);
  };

  const handleToggleStatus = (status: ActionPotStatus) => {
    const next = selectedStatuses.includes(status)
      ? selectedStatuses.filter(s => s !== status)
      : [...selectedStatuses, status];
    setStatuses(next);
  };

  const handleRowClick = (potId: string) => {
    navigate(routeBuilders.finance.potDetail(potId));
  };

  if (isLoading) {
    return <LoadingSkeleton />;
  }

  const totalCount = data?.totalCount ?? 0;

  return (
    <Card data-testid="open-pots-widget" className="min-w-0 flex-1">
      <CardContent className="flex flex-col gap-2 p-6">
        <div className="flex items-center justify-between">
          <p className="type-body-lg font-bold">Open Pots</p>
          <p
            className="type-body-lg font-bold text-content-secondary"
            data-testid="open-pots-total-count"
          >
            {totalCount}
          </p>
        </div>

        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              data-testid="open-pots-status-filter"
              className="min-w-[200] justify-between self-start"
            >
              {renderStatusValue(selectedStatuses)}
              <ChevronDownIcon aria-hidden="true" className="size-4" />
            </Button>
          </PopoverTrigger>
          <PopoverContent align="start" className="p-2">
            <label
              data-testid="open-pots-status-option-all"
              className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 type-body-md hover:bg-surface-raised"
            >
              <Checkbox
                checked={selectedStatuses.length === ACTION_STATUSES.length}
                onCheckedChange={handleToggleAll}
                aria-label="All statuses"
                className="pointer-events-none"
              />
              All statuses
            </label>
            {ACTION_STATUSES.map(status => (
              <label
                key={status}
                data-testid={`open-pots-status-option-${status}`}
                className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 type-body-md hover:bg-surface-raised"
              >
                <Checkbox
                  checked={selectedStatuses.includes(status)}
                  onCheckedChange={() => handleToggleStatus(status)}
                  aria-label={getStatusPickerLabel(status)}
                  className="pointer-events-none"
                />
                {getStatusPickerLabel(status)}
              </label>
            ))}
          </PopoverContent>
        </Popover>

        <div
          className="max-h-[420px] overflow-y-auto"
          data-testid="open-pots-list"
        >
          {weeks.length === 0 ? (
            <EmptyState />
          ) : (
            <div className="flex flex-col gap-4">
              {weeks.map(week => (
                <WeekSection
                  key={`${week.periodYear}-${week.periodWeekNumber}`}
                  week={week}
                  onRowClick={handleRowClick}
                />
              ))}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function parseStatuses(value: string | null): ActionPotStatus[] {
  if (value === null) {
    return [...ACTION_STATUSES];
  }
  return value
    .split(',')
    .filter((s): s is ActionPotStatus =>
      (ACTION_STATUSES as string[]).includes(s)
    );
}

function renderStatusValue(selected: ActionPotStatus[]): string {
  if (selected.length === ACTION_STATUSES.length) {
    return 'All statuses';
  }
  if (selected.length === 0) {
    return 'No statuses';
  }
  return selected.map(getStatusPickerLabel).join(', ');
}

function getStatusPickerLabel(status: ActionPotStatus): string {
  if (status === 'MissedSubmitDeadline') {
    return 'Missed Deadline';
  }
  if (status === 'MissedVerificationDeadline') {
    return 'Missed Verification';
  }
  return status;
}

interface WeekSectionProps {
  week: OpenPotsWeekGroup;
  onRowClick: (potId: string) => void;
}

function WeekSection({ week, onRowClick }: WeekSectionProps) {
  const weekLabel = formatWeekLabel([
    {
      year: week.periodYear,
      weekNumber: week.periodWeekNumber,
      periodStart: week.weekCommencing,
      periodEnd: week.weekCommencing,
    },
  ]);
  const weekCommencingLabel = format(parseISO(week.weekCommencing), 'd MMM');

  return (
    <div
      data-testid={`open-pots-week-${week.periodYear}-${week.periodWeekNumber}`}
    >
      <div className="sticky top-0 z-[1] bg-surface-default py-0.5">
        <span className="type-caption font-semibold text-content-secondary">
          {weekLabel} · w/c {weekCommencingLabel}
        </span>
      </div>
      <div>
        {week.pots.map((pot, index) => (
          <PotRow
            key={pot.potId}
            pot={pot}
            isLast={index === week.pots.length - 1}
            onClick={onRowClick}
          />
        ))}
      </div>
    </div>
  );
}

interface PotRowProps {
  pot: OpenPotDto;
  isLast: boolean;
  onClick: (potId: string) => void;
}

function PotRow({ pot, isLast, onClick }: PotRowProps) {
  return (
    <button
      type="button"
      onClick={() => onClick(pot.potId)}
      data-testid={`open-pot-row-${pot.potId}`}
      className="block w-full text-left hover:bg-surface-raised"
    >
      <div className="pl-2">
        <TreeConnectorBox isLast={isLast}>
          <div className="flex min-h-10 items-center gap-2 py-1.5 pr-2">
            <PotStatusBadge status={pot.status} />
            <div className="min-w-0 flex-1">
              <div className="flex min-w-0 items-center gap-0.5">
                {pot.isGang ? (
                  <GroupsIcon
                    aria-hidden="true"
                    className="size-4 shrink-0 text-content-secondary"
                  />
                ) : (
                  <PersonIcon
                    aria-hidden="true"
                    className="size-4 shrink-0 text-content-secondary"
                  />
                )}
                <span className="type-body-md truncate font-semibold">
                  {pot.claimantName || 'Unnamed pot'}
                </span>
              </div>
              <span className="type-caption block truncate text-content-secondary">
                {getNextAction(pot.status)}
              </span>
            </div>
            <span className="type-body-md min-w-18 shrink-0 text-right font-semibold">
              {formatCurrency(pot.grossValue)}
            </span>
          </div>
        </TreeConnectorBox>
      </div>
    </button>
  );
}

function EmptyState() {
  return (
    <div className="py-8 text-center" data-testid="open-pots-empty">
      <p className="type-body-md text-content-secondary">No pots need action</p>
    </div>
  );
}

function LoadingSkeleton() {
  return (
    <Card data-testid="open-pots-widget-loading" className="min-w-0 flex-1">
      <CardContent className="p-6">
        <Skeleton width={220} height={24} className="mb-4" />
        <Skeleton width="100%" height={32} className="mb-4" />
        <div className="flex flex-col gap-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} height={32} />
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
